/**
 * @file src/components/UserControls/UserControlsStyles.tsx
 * @exports AvatarBox, CaretBox, OrgSwitcherBox, ProfileTrigger, UserControlsBase
 * @summary styled-components for UserControls component
 */

import { colors, focusAfter } from "@cerebruminc/cerebellum";
import { styled } from "styled-components";
import type { AvatarBoxProps, ProfileTriggerProps, UserControlsBaseProps } from "./types";

export const UserControlsBase = styled.div<UserControlsBaseProps>`
  align-items: center;
  background: ${({ $white }) => ($white ? colors.WHITE : "linear-gradient(180deg, #5377ec 0%, #c0aaff 100%)")};
  display: flex;
  gap: 22px;
  height: 100%;
  padding: 0 28px 0 30px;
`;

// The organization switcher and the profile menu are separate targets, so the
// switcher sits beside the profile trigger rather than inside it.
//
// No overflow: hidden here. Mantine draws a button's focus ring as an outline 2px outside the
// button, so clipping this box hides the switcher's focus state entirely. min-width: 0 is what
// lets the flex item shrink; the ellipsis belongs to the Truncate inside it, which clips itself.
export const OrgSwitcherBox = styled.div`
  display: flex;
  min-width: 0;
  @media all and (max-width: 640px) {
    max-width: 45vw;
  }
`;

export const ProfileTrigger = styled.div<ProfileTriggerProps>`
  align-items: center;
  display: flex;
  gap: 22px;
  height: 100%;
  pointer-events: ${({ $clickable }) => ($clickable ? "inherit" : "none")};
  &:focus-visible {
    outline: none;
  }
`;

export const AvatarBox = styled.div<AvatarBoxProps>`
  background-color: transparent;
  height: 50px;
  padding: 0;
  position: relative;
  width: 50px;
`;

export const CaretBox = styled.div<{ $white?: boolean }>`
  border-radius: 50%;
  height: 20px;
  position: relative;
  width: 20px;
  ${({ $white }) =>
		focusAfter({
			borderRadius: 50,
			focusColor: $white ? colors.BLUE_70 : colors.WHITE,
			offset: -10,
		})}

  &:after {
    top: -11px;
    bottom: -9px;
  }
  ${ProfileTrigger}:focus-visible & {
    outline: none;
    &:after {
      border: 1px solid
        ${({ $white }) => ($white ? colors.BLUE_70 : colors.WHITE)};
    }
  }
`;
